export const DEAL_CATEGORY_IDS = {
  INVOICE_FINANCING: "DCT1000001",
  JOINT_VENTURES: "DCT1000002",
  STARTUP_FUNDING: "DCT1000003",
  SUPPLY_CHAIN_FINANCE: "DCT1000006",
};

export const RESTRICTED_CATEGORY_STATUS_IDS = {
  DELINQUENT: "USM1000004",
  DEACTIVATED: "USM1000005",
  BLACKLISTED: "USM1000011",
};

export const RESTRICTED_STATUS_LABELS = {
  [RESTRICTED_CATEGORY_STATUS_IDS.DEACTIVATED]: "Deactivated",
  [RESTRICTED_CATEGORY_STATUS_IDS.DELINQUENT]: "Delinquent",
  [RESTRICTED_CATEGORY_STATUS_IDS.BLACKLISTED]: "Blacklisted",
};

export function isCategoryRestricted(statusId) {
  return (
    statusId === RESTRICTED_CATEGORY_STATUS_IDS.DEACTIVATED ||
    statusId === RESTRICTED_CATEGORY_STATUS_IDS.DELINQUENT ||
    statusId === RESTRICTED_CATEGORY_STATUS_IDS.BLACKLISTED
  );
}

export function getUnrestrictedDealCategories(categories = []) {
  return (categories || []).filter((category) => !isCategoryRestricted(category?.statusId));
}

export function pickAccessibleCategory(categories = [], preferredId = null) {
  const valid = getUnrestrictedDealCategories(categories);
  if (valid.length === 0) return null;
  if (preferredId) {
    const preferred = valid.find((category) => category.dealCategoryId === preferredId);
    if (preferred) return preferred;
  }
  return (
    valid.find((category) => category.dealCategoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING) ||
    valid[0]
  );
}

export const PRODUCT_GROUPS = {
  FINANCING: "financing",
  VENTURES: "ventures",
  EQUITY: "equity",
};

/** Nested choices under Financing on the enroll screen */
export const FINANCING_SUBCATEGORY_OPTIONS = [
  {
    key: "invoice",
    label: "Invoice",
    dealCategoryId: DEAL_CATEGORY_IDS.INVOICE_FINANCING,
  },
  {
    key: "supply-chain",
    label: "Supply Chain",
    dealCategoryId: DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE,
  },
];

/** Top-level choices shown on enroll / new enquiry */
export const PRODUCT_GROUP_OPTIONS = [
  {
    key: PRODUCT_GROUPS.FINANCING,
    label: "Financing",
    dealCategoryId: null,
    requiresSubCategory: true,
    children: FINANCING_SUBCATEGORY_OPTIONS,
  },
  {
    key: PRODUCT_GROUPS.VENTURES,
    label: "Ventures",
    dealCategoryId: DEAL_CATEGORY_IDS.JOINT_VENTURES,
    requiresSubCategory: false,
  },
  {
    key: PRODUCT_GROUPS.EQUITY,
    label: "Equity",
    dealCategoryId: DEAL_CATEGORY_IDS.STARTUP_FUNDING,
    requiresSubCategory: false,
  },
];

export const FINANCING_SUBCATEGORY_IDS = [
  DEAL_CATEGORY_IDS.INVOICE_FINANCING,
  DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE,
];

export const DEAL_CATEGORY_DISPLAY_NAMES = {
  [DEAL_CATEGORY_IDS.INVOICE_FINANCING]: "Invoice Financing",
  [DEAL_CATEGORY_IDS.JOINT_VENTURES]: "Ventures",
  [DEAL_CATEGORY_IDS.STARTUP_FUNDING]: "Equity",
  [DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE]: "Supply Chain Financing",
};

export function getDealCategoryDisplayName(dealCategoryId, fallbackName = "") {
  return DEAL_CATEGORY_DISPLAY_NAMES[dealCategoryId] || fallbackName || "";
}

export function isFinancingCategory(dealCategoryId) {
  return FINANCING_SUBCATEGORY_IDS.includes(dealCategoryId);
}

/** Invoice Financing and Supply Chain Financing share the IF-style form fields */
export function isInvoiceStyleCategory(dealCategoryId) {
  return (
    dealCategoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING ||
    dealCategoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE
  );
}

export function isSupplyChainCategory(dealCategoryId) {
  if (!dealCategoryId) return false;
  if (dealCategoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE) return true;
  const normalized = String(dealCategoryId).toLowerCase().replace(/[\s_-]+/g, "");
  return (
    normalized === "dct1000006" ||
    normalized.includes("supplychain")
  );
}

export function isVenturesCategory(dealCategoryId) {
  return dealCategoryId === DEAL_CATEGORY_IDS.JOINT_VENTURES;
}

export function isEquityCategory(dealCategoryId) {
  return dealCategoryId === DEAL_CATEGORY_IDS.STARTUP_FUNDING;
}

export function getFinancingSubcategoryOptions(categories = []) {
  return (categories || [])
    .filter((category) =>
      FINANCING_SUBCATEGORY_IDS.includes(category.dealCategoryId),
    )
    .map((category) => ({
      value: category.dealCategoryId,
      label: getDealCategoryDisplayName(
        category.dealCategoryId,
        category.dealCategoryName,
      ),
    }));
}

export function getProductGroupOptions() {
  return PRODUCT_GROUP_OPTIONS.map((option) => ({
    value: option.key,
    label: option.label,
  }));
}

export function resolveDealCategoryIdForProductGroup(productGroup) {
  const option = PRODUCT_GROUP_OPTIONS.find((item) => item.key === productGroup);
  return option?.dealCategoryId || null;
}
